import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, Input, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { take } from 'rxjs';
import { AiIsLoadingComponent } from '../../../basic/ai/ai-is-loading/ai-is-loading.component';
import { TruncatePipe } from '../../../basic/pipes/truncate.pipe';
import { Objection } from '../../../core/objection/objection';
import { ObjectionService } from '../../../core/objection/objection.service';
import { OpenaiService } from '../../../core/openai/openai.service';
import { SessionMessage } from '../../../core/session-message/session-message';
import { SessionMessageService } from '../../../core/session-message/session-message.service';
import { SessionTemplateService } from '../../../core/session-template/session.template.service';
import { Session } from '../../../core/session/session';
import { FeedbackImprovementsComponent } from '../../../domain/feedback/feedback-improvements/feedback-improvements.component';

@Component({
  selector: 'app-customer-time-range',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,

    NzButtonModule,
    NzIconModule,
    NzPopoverModule,
    NzToolTipModule,
    NzTabsModule,
    NzSkeletonModule,
    NzModalModule,

    AiIsLoadingComponent,
    FeedbackImprovementsComponent,

    TruncatePipe,
  ],
  templateUrl: './customer-time-range.component.html',
  styleUrl: './customer-time-range.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerTimeRangeComponent {
  @ViewChild('audioPlayer') audioPlayer!: ElementRef<HTMLAudioElement>;

  @Input({ required: true }) session!: Session;

  @Input({ required: true }) index!: number;

  @Input({ required: true }) range!: {
    startTime: DateTime<boolean>;
    endTime: DateTime<boolean>;
    message: string;
    sessionMessage: SessionMessage;
  };

  @Input({ required: true }) leftPx!: string;
  @Input({ required: true }) widthPx!: string;

  @Input({ required: true }) sessionMessageList: SessionMessage[] = [];

  isLoading = signal<boolean>(true);

  isPopoverVisible = false;

  constructor(
    private msg: NzMessageService,
    private objectionService: ObjectionService,
    private sessionMessageService: SessionMessageService,
    private sessionTemplateService: SessionTemplateService,
    private openaiService: OpenaiService
  ) {}

  objectionList = toSignal(this.objectionService.itemList$(), { initialValue: [] });
  sessionTemplateList = toSignal(this.sessionTemplateService.itemList$(), { initialValue: [] });

  emotionalObjectionList = computed<Objection[]>(() => {
    const itemList = this.objectionList();

    const result: Objection[] = [];

    const sessionTemplateList = this.sessionTemplateList();

    const sessionTemplate = sessionTemplateList.find((item) => item.id === this.session.subData?.template?.id);

    sessionTemplate?.unsafeSubData?.emotionalObjectionIdList?.forEach((id) => {
      const item = itemList.find((item) => item.id === id);
      if (item) {
        result.push(item);
      }
    });

    return result;
  });

  factObjectionList = computed<Objection[]>(() => {
    const itemList = this.objectionList();

    const result: Objection[] = [];

    const sessionTemplateList = this.sessionTemplateList();

    const sessionTemplate = sessionTemplateList.find((item) => item.id === this.session.subData?.template?.id);

    sessionTemplate?.unsafeSubData?.factObjectionIdList?.forEach((id) => {
      const item = itemList.find((item) => item.id === id);
      if (item) {
        result.push(item);
      }
    });

    return result;
  });

  get feedbackIndex() {
    return this.index;
  }

  get feedbackRange() {
    const result = this.sessionMessageList.map((message) => {
      return {
        startTime: DateTime.fromISO(message.subData.salesStartTimeStr),
        endTime: DateTime.fromISO(message.subData.salesEndTimeStr),
        message: message.subData.salesMessage,
        sessionMessage: message,
      };
    });

    if (this.feedbackIndex < result.length) {
      return result[this.feedbackIndex];
    }

    return undefined;
  }

  handleCancel() {
    this.isPopoverVisible = false;
  }

  onFindObjectionClick() {
    const sessionTemplate = this.sessionTemplateList().find((item) => item.id === this.session.subData?.template?.id);

    this.sessionMessageService.findObjection$(sessionTemplate!, this.range.sessionMessage).pipe(take(1));
  }

  onRangeClick() {
    this.isPopoverVisible = true;
  }

  onPlayClick() {
    this.playAudio();
  }

  private playAudio() {
    if (this.audioPlayer && this.audioPlayer.nativeElement) {
      this.stopAudio();
      const currentSession = this.sessionMessageList[this.index];

      this.audioPlayer.nativeElement.src = currentSession.subData.customerAudioUrl!;
      const playPromise = this.audioPlayer.nativeElement.play();

      if (playPromise !== undefined) {
        playPromise
          .then((_) => {
            // Playback started successfully
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              this.msg.warning('Interrupted by a call to pause.');
              console.log('Play request was interrupted, likely due to a pause() call');
            } else {
              this.msg.warning('Error during audio playback.');
              console.error('Error during audio playback:', error);
            }
          });
      }
    }
  }

  stopAudio() {
    if (this.audioPlayer && this.audioPlayer.nativeElement) {
      this.audioPlayer.nativeElement.pause();
      this.audioPlayer.nativeElement.currentTime = 0;
    }
  }
}
